import { useState } from 'react';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';

export interface UseDeleteEntityPromptArgs {
  entityId: string;
  /** Dry run of the delete; rejects with a not-deletable error when the entity is in use. */
  canDelete: (entityId: string) => Promise<void>;
  deleteEntity: (entityId: string) => Promise<void>;
  isNotDeletableError: (err: unknown) => err is Error;
  genericErrorMessage: string;
  onDeleted: () => void | Promise<void>;
  onCheckError?: (error: unknown) => void;
  onDeleteError?: (error: unknown) => void;
}

export const useDeleteEntityPrompt = ({
  entityId,
  canDelete,
  deleteEntity,
  isNotDeletableError,
  genericErrorMessage,
  onDeleted,
  onCheckError,
  onDeleteError,
}: UseDeleteEntityPromptArgs) => {
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState(false);
  const [isInUsePromptVisible, setIsInUsePromptVisible] = useState(false);
  const [inUseErrorMessage, setInUseErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingCanDelete, setIsCheckingCanDelete] = useState(false);
  const { showSnackbar } = useSnackbar();

  const showInUsePrompt = (err: Error) => {
    setInUseErrorMessage(err.message);
    setIsInUsePromptVisible(true);
  };

  // The Delete option keeps its ellipsis menu open while the dry run runs, but ItemCard and
  // BreadcrumbHeading don't expose the menu's open state, so Escape is the only way to dismiss it. It
  // must close before a prompt opens or its pointer-events lock blocks the prompt. Only the menu that
  // started the check is closed: if the user already dismissed it, a synthetic Escape would dismiss
  // whatever overlay is on top instead.
  const closeEllipsisMenu = (originatingMenu: Element | null) => {
    if (!originatingMenu?.isConnected) return;
    originatingMenu.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  };

  const openDeletePrompt = async (event?: Event) => {
    if (isCheckingCanDelete) return;
    const originatingMenu = event?.target instanceof Element ? event.target.closest('[role="menu"]') : null;
    try {
      setIsCheckingCanDelete(true);
      await canDelete(entityId);
      closeEllipsisMenu(originatingMenu);
      setIsInUsePromptVisible(false);
      setIsRemovePromptVisible(true);
    } catch (err) {
      closeEllipsisMenu(originatingMenu);
      if (isNotDeletableError(err)) {
        showInUsePrompt(err);
        return;
      }
      onCheckError?.(err);
      showSnackbar({ status: SnackbarStatus.ERROR, message: genericErrorMessage });
    } finally {
      setIsCheckingCanDelete(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      await deleteEntity(entityId);
      setIsSubmitting(false);
      setIsRemovePromptVisible(false);
      await onDeleted();
    } catch (err) {
      setIsSubmitting(false);
      setIsRemovePromptVisible(false);
      if (isNotDeletableError(err)) {
        showInUsePrompt(err);
        return;
      }
      onDeleteError?.(err);
      showSnackbar({ status: SnackbarStatus.ERROR, message: genericErrorMessage });
    }
  };

  return {
    isRemovePromptVisible,
    isInUsePromptVisible,
    inUseErrorMessage,
    isSubmitting,
    isCheckingCanDelete,
    openDeletePrompt,
    closeDeletePrompt: () => setIsRemovePromptVisible(false),
    closeInUsePrompt: () => setIsInUsePromptVisible(false),
    confirmDelete,
  };
};

export type DeleteEntityPrompt = ReturnType<typeof useDeleteEntityPrompt>;
